import { useEffect, useRef, useState } from 'react';
import { Wand2 } from 'lucide-react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { canvasToBlob, loadImage } from '../lib/qrWallpaper.js';

const W = 1200;
const H = 400;

interface Props {
  /** URL of the separately stored logo. */
  logoUrl: string | null | undefined;
  initialColor?: string;
  /** Receives the rendered banner; resolves once it is uploaded. */
  onApply: (banner: Blob) => Promise<void>;
}

/** Builds the 3:1 card banner from the stored logo on a freely chosen background colour. */
export default function BannerGenerator({ logoUrl, initialColor, onApply }: Props) {
  const { t } = useAppTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [color, setColor] = useState(/^#[0-9a-fA-F]{6}$/.test(initialColor ?? '') ? initialColor! : '#ffffff');
  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLogo(null);
    if (logoUrl) loadImage(logoUrl).then((i) => { if (alive) setLogo(i); }).catch(() => undefined);
    return () => { alive = false; };
  }, [logoUrl]);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    c.width = W;
    c.height = H;
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, W, H);
    if (logo && logo.naturalWidth > 0) {
      const r = Math.min((W * 0.6) / logo.naturalWidth, (H * 0.55) / logo.naturalHeight);
      const w = logo.naturalWidth * r;
      const h = logo.naturalHeight * r;
      ctx.drawImage(logo, (W - w) / 2, (H - h) / 2, w, h);
    }
  }, [color, logo]);

  async function apply() {
    const c = canvasRef.current;
    if (!c) return;
    setBusy(true);
    try {
      await onApply(await canvasToBlob(c, 'image/png'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-xl border border-line p-4">
      <div>
        <h4 className="text-sm font-semibold text-ink">{t('bannerGen.title', 'Banner aus Logo erstellen')}</h4>
        <p className="text-xs text-ink-faint">{t('bannerGen.hint', 'Das Logo wird zentriert auf deine Hintergrundfarbe gesetzt und als Banner übernommen.')}</p>
      </div>
      <canvas ref={canvasRef} role="img" aria-label={t('bannerGen.preview', 'Banner-Vorschau')} className="aspect-[3/1] w-full rounded-lg border border-line" />
      {!logoUrl && <p className="text-xs text-ink-muted">{t('bannerGen.noLogo', 'Lade zuerst oben ein Logo hoch.')}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="banner-gen-color" className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{t('bannerGen.color', 'Hintergrundfarbe')}</label>
        <input id="banner-gen-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-12 cursor-pointer rounded-lg border border-line bg-transparent p-1" />
        <span className="font-mono text-xs text-ink-faint">{color}</span>
        <button
          type="button"
          disabled={!logo || busy}
          onClick={apply}
          className="ml-auto inline-flex items-center gap-2 rounded-lg bg-accent-600 px-3 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50 transition-colors"
        >
          <Wand2 className="h-4 w-4" aria-hidden="true" /> {t('bannerGen.apply', 'Als Banner übernehmen')}
        </button>
      </div>
    </div>
  );
}
